import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  NamedLink,
  IconArrowHead,
} from '../../../../../components';
import MegaMenuSearch from './MegaMenuSearch';
import { fetchBrands } from '../../../../../util/api';
import { brandsFromApiResponse } from '../../../../../util/brands';

import css from './MegaMenu.module.css';

/**
 * Brands mega dropdown menu component
 * Shows a wide panel with brand options that redirects to search page
 */
const BrandsMegaMenu = ({
  currentPage,
  intl,
  history,
  isOpen,
  onRequestOpen,
  onRequestClose,
}) => {
  const [allBrands, setAllBrands] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchBrands()
      .then(response => {
        if (cancelled) return;
        setAllBrands(brandsFromApiResponse(response));
      })
      .catch(() => {
        if (cancelled) return;
        setAllBrands([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredBrands = useMemo(() => {
    if (!searchTerm.trim()) {
      return allBrands;
    }

    const searchLower = searchTerm.toLowerCase();
    return allBrands.filter(
      brand =>
        brand.brand.toLowerCase().includes(searchLower) ||
        brand.value.toLowerCase().includes(searchLower)
    );
  }, [searchTerm, allBrands]);

  const handleSearch = term => {
    setSearchTerm(term);
  };

  const handleBrandClick = brandValue => {
    history.push(`/products/${brandValue}`);
    onRequestClose && onRequestClose();
  };

  return (
    <Menu
      contentPlacementOffset={-12}
      contentPosition="left"
      isOpen={isOpen}
      onToggleActive={nextOpen =>
        nextOpen ? onRequestOpen && onRequestOpen() : onRequestClose && onRequestClose()
      }
    >
      <MenuLabel
        className={classNames(css.megaMenuLabel, currentPage === 'BrandsPage' && css.currentPage)}
        isOpenClassName={css.megaMenuIsOpen}
        onMouseEnter={() => onRequestOpen && onRequestOpen()}
        onMouseLeave={() => onRequestClose && onRequestClose()}
      >
        <span className={css.megaMenuLabelWrapper}>
          {intl.formatMessage({ id: 'TopbarDesktop.brands.label' })}
          <IconArrowHead
            direction="down"
            size="small"
            rootClassName={css.arrowIcon}
            ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.brands.label' })}
          />
        </span>
      </MenuLabel>
      <MenuContent
        className={css.megaMenuContent}
        onMouseEnter={() => onRequestOpen && onRequestOpen()}
        onMouseLeave={() => onRequestClose && onRequestClose()}
      >
        <MenuItem key="brands-header" className={css.megaMenuHeaderItem}>
          <div className={css.megaMenuHeader}>
            <div>
              <h3 className={css.megaMenuTitle}>
                {intl.formatMessage({ id: 'TopbarDesktop.brands.title' })}
              </h3>
              <p className={css.megaMenuSubtitle}>
                {intl.formatMessage({ id: 'TopbarDesktop.brands.subtitle' })}
              </p>
            </div>
            <NamedLink className={css.viewAllLink} name="BrandsPage">
              {intl.formatMessage({ id: 'TopbarDesktop.brands.viewAll' })}
            </NamedLink>
          </div>
        </MenuItem>

        <MenuItem key="brands-search" className={css.megaMenuSearchItem}>
          <MegaMenuSearch
            inputId="mega-menu-brands-search"
            inputName="brands-search"
            placeholder="Rechercher des marques..."
            onSearch={handleSearch}
          />
        </MenuItem>

        <MenuItem key="brands-grid" className={css.megaMenuGridWrapper}>
          <div className={css.megaMenuGrid}>
            {filteredBrands.map((brand, index) => (
              <button
                key={`${brand.value}_${index}`}
                className={css.megaMenuItem}
                onClick={() => handleBrandClick(brand.value)}
              >
                <div
                  className={classNames(
                    css.brandLogoContainer,
                    brand.logoOnDark && css.brandLogoContainerDark
                  )}
                >
                  {brand.logo_url ? (
                    <>
                      <img
                        src={brand.logo_url}
                        alt={`${brand.brand} logo`}
                        className={css.brandLogo}
                        onError={e => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling && e.target.nextSibling.style) {
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                      <span className={css.brandFallback} style={{ display: 'none' }}>
                        {brand.brand.charAt(0).toUpperCase()}
                      </span>
                    </>
                  ) : (
                    <span className={css.brandFallback}>
                      {brand.brand.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <span className={css.brandName}>{brand.brand}</span>
              </button>
            ))}
          </div>
          {filteredBrands.length === 0 && (
            <div className={css.noResults}>
              <p>Aucune marque trouvée pour « {searchTerm} »</p>
            </div>
          )}
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

export default BrandsMegaMenu;
